// src/components/AppLauncher/hooks/useDeepLinking.js
import { useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSpaces } from '../../../contexts/SpacesContext';

const useDeepLinking = ({ apps, launchApp, closeWindow, activeApps }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { spaces, setActiveSpaceId } = useSpaces();
  const attemptedPlugins = useRef(new Set());
  const closingPluginId = useRef(null);

  const getPluginFromUrl = useCallback(() => {
    const path = location.pathname;
    const match = path.match(/^\/plugins\/([^\/]+)/);
    return match ? match[1] : null;
  }, [location.pathname]);

  const getSpaceFromUrl = useCallback(() => {
    const path = location.pathname;
    
    // Handle /spaces/{name} format
    const spaceMatch = path.match(/^\/spaces\/([^\/]+)/);
    if (spaceMatch) return spaceMatch[1];
    
    // Handle legacy routes
    if (path === '/news' || path === '/newsfeed') return 'newsfeed';
    if (path === '/apps') return 'apps';
    
    return null;
  }, [location.pathname]);

  const updateUrl = useCallback((appId) => {
    const currentPath = location.pathname;
    if (appId === null) {
      if (currentPath !== '/') {
        window.history.pushState({}, '', '/');
      }
    } else {
      const target = `/plugins/${appId}`;
      if (currentPath !== target) {
        window.history.pushState({ appId }, '', target);
      }
    }
  }, [location.pathname]);

  const navigateToSpace = useCallback((spaceName) => {
    const slug = spaceName.toLowerCase().replace(/\s+/g, '-');
    navigate(`/spaces/${slug}`);
  }, [navigate]);

  // Handle space-based URL routing
  useEffect(() => {
    const spaceName = getSpaceFromUrl();
    if (spaceName && spaces.length > 0) {
      // Try to match by slug (kebab-case)
      const space = spaces.find(
        s => s.name.toLowerCase().replace(/\s+/g, '-') === spaceName.toLowerCase()
      );
      if (space) {
        setActiveSpaceId(space.space_id);
      }
    }
  }, [getSpaceFromUrl, spaces, setActiveSpaceId]);

  // Handle plugin deep linking
  useEffect(() => {
    const pluginId = getPluginFromUrl();
    if (!pluginId) return;

    if (pluginId === closingPluginId.current) return;
    if (attemptedPlugins.current.has(pluginId)) return;
    if (!apps || apps.length === 0) return;
    if (activeApps.some(app => app.id === pluginId)) {
      attemptedPlugins.current.add(pluginId);
      return;
    }

    const app = apps.find(a => a.id === pluginId);
    if (app) {
      attemptedPlugins.current.add(pluginId);
      launchApp(app);
    }
  }, [apps, activeApps, getPluginFromUrl, launchApp]);

  return { updateUrl, navigateToSpace };
};

export default useDeepLinking;
